import type { Method } from '@luna/api';
import { getEnv } from '@luna/env';

import { IMAGE_VARIANT } from './ImageVariantsService';
import { addToCache, getCached } from './byteImageCache';
import { fetchByteImage } from './fetchByteImage';

const updateURLWithAPI = (url: string) => getEnv().georgeApiUrl + url;

const updateLocalDevURL = (url: string) => url.replace(/^.*\/g\/api\//, getEnv().georgeApiUrl);

const updateUrl = (url: string) => {
    if (url.indexOf('//') === -1) {
        return updateURLWithAPI(url);
    }
    if (__DEV__) {
        // from localhost you can't fetch image from server if url is not https://localhost....
        // which is not because it's from endpoints so we transform it
        return updateLocalDevURL(url);
    }
    return url;
};

export function urlToVariant(url: string, variant: IMAGE_VARIANT): string {
    const variantRegex = /([?&]variant=)[a-z]+/;

    if (url.match(variantRegex)) {
        return url.replace(variantRegex, `$1${variant}`);
    }

    const paramSeparator = url.indexOf('?') === -1 ? '?' : '&';

    return `${url + paramSeparator}variant=${variant}`;
}

interface GetGeorgeByteOptions {
    useCache?: boolean;
    variant?: IMAGE_VARIANT;
    method?: Method;
    sendAcceptHeader?: boolean;
    sendImageVariantParam?: boolean;
}

export async function getGeorgeByteImage(
    url: string,
    cb: (base64Image: string) => void,
    {
        useCache = false,
        variant = IMAGE_VARIANT.MOBILE,
        method = 'GET',
        sendAcceptHeader = true,
        sendImageVariantParam = true,
    }: GetGeorgeByteOptions = {},
): Promise<void> {
    const fetchByteImageUrl = sendImageVariantParam
        ? urlToVariant(updateUrl(url), variant)
        : updateUrl(url);

    const imageVariantCacheId = sendImageVariantParam ? variant : IMAGE_VARIANT.WITHOUT;

    if (useCache) {
        const cachedImagePromise = getCached(url, imageVariantCacheId);
        if (cachedImagePromise) {
            const image = await cachedImagePromise;
            if (image) {
                cb(image);
                return;
            }
        }
    }

    const imagePromise = fetchByteImage(fetchByteImageUrl, sendAcceptHeader, method);

    if (useCache) {
        addToCache(url, imageVariantCacheId, imagePromise);
    }

    cb(await imagePromise);
}
